import ActionType from 'actions/ActionType'
import { Auth } from 'utils/Auth'
import { DATA_API_URL } from 'utils/AllURLs'
import { getBrandRegistrationErrors } from './index'
import { combineReducers } from 'redux'

// Action Types
const UPDATE_ENTITY = 'UPDATE_ENTITY'
const UPDATE_FORM_FIELD = 'UPDATE_FORM_FIELD'
const UPDATE_MESSAGE_SAMPLES = 'UPDATE_MESSAGE_SAMPLES'

// Unified action for updating campaigns or useCaseDrafts, including message samples
export const updateEntity = (entityType, key, data) => dispatch =>
  dispatch({
    type: UPDATE_ENTITY,
    payload: { entityType, key, data },
  })

// Action for updating form fields
export const updateFormField = (fieldName, value) => ({
  type: UPDATE_FORM_FIELD,
  payload: { fieldName, value },
})

// Action for updating message samples within an entity (campaign or useCaseDraft)
export const updateMessageSamples =
  (entityType, key, sampleIndex, sample) => dispatch =>
    dispatch({
      type: UPDATE_MESSAGE_SAMPLES,
      payload: { entityType, key, sampleIndex, sample },
    })

export const updateA2p10dlc =
  ({ entity }) =>
  dispatch =>
    new Promise((resolve, reject) => {
      dispatch({
        type: ActionType.Data.UPDATE_REQUEST,
        entity,
        entityType: 'a2p10dlc',
      })

      return Auth.currentSession().then(session => {
        const headerValue = `Bearer ${session.idToken.jwtToken}`
        const errors = getBrandRegistrationErrors({ data: { a2p10dlc: { [entity.phone_number]: entity } }, loadStatus: 'SUCCESS' })
        const url = `${DATA_API_URL}/a2p10dlc${errors?.step === 2 ? '?directive=step_2' : ''}`

        return fetch(url, {
          cache: 'no-cache',
          headers: {
            'content-type': 'application/json',
            authorization: headerValue,
          },
          method: 'PUT',
          mode: 'cors',
          redirect: 'follow',
          referrer: 'no-referrer',
          body: JSON.stringify(entity),
        })
          .then(res => {
            return res.json().then(data => {
              if (!res.ok) {
                return { notOk: true, ...data }
              }
              return data
            })
          })
          .then(
            payload => {
              if (payload && payload.notOk) {
                dispatch({
                  type: ActionType.Data.UPDATE_FAILURE,
                  payload,
                  entityType: 'a2p10dlc',
                  notOk: true,
                })
                reject(payload)
                return
              }

              dispatch({
                type: ActionType.Data.UPDATE_SUCCESS,
                payload: entity ? payload : null,
                entityType: 'a2p10dlc',
                snackbarMessage: 'Successfully updated compliance information!',
              })
              resolve(payload)
            },
            error => {
              dispatch({
                type: ActionType.Data.UPDATE_FAILURE,
                payload: error,
                entityType: 'a2p10dlc',
              })
              reject(error)
            },
          )
      })
    })

// Entity reducer (campaigns and useCaseDrafts)
const entityReducer = (
  state = { campaigns: {}, useCaseDrafts: {} },
  action,
) => {
  switch (action.type) {
    case ActionType.Data.LIST_SUCCESS:
      if (action.entityType === 'a2p10dlc') {
        const campaigns = action?.payload?.[0]?.campaigns || {}
        const useCaseDrafts = action?.payload?.[0]?.useCaseDrafts || {}

        return { campaigns, useCaseDrafts }
      }
      return state
    case UPDATE_ENTITY: {
      const { entityType, key, data } = action.payload
      return {
        ...state,
        [entityType]: {
          ...state[entityType],
          [key]: data,
        },
      }
    }
    case UPDATE_MESSAGE_SAMPLES: {
      const { entityType, key, sampleIndex, sample } = action.payload
      const entity = state[entityType]?.[key] || {}
      const updatedSamples = entity?.messageSamples
        ? [...entity.messageSamples]
        : []

      if (sample === undefined) {
        // If undefined, remove the sample
        updatedSamples.splice(sampleIndex, 1)
      } else if (sampleIndex >= 0) {
        // Update existing sample
        updatedSamples[sampleIndex] = sample
      } else {
        // Add new sample
        updatedSamples.push(sample)
      }

      return {
        ...state,
        [entityType]: {
          ...state[entityType],
          [key]: {
            ...entity,
            messageSamples: updatedSamples,
          },
        },
      }
    }
    default:
      return state
  }
}

// Form field reducer
const formFieldReducer = (state = {}, action) => {
  switch (action.type) {
    case ActionType.Data.LIST_SUCCESS:
      if (action.entityType === 'a2p10dlc' && action.payload?.[0]) {
        const payload = action.payload?.[0]

        const {
          campaigns,
          created_date,
          messagingServices,
          brands,
          id,
          trustProducts,
          useCaseDrafts,
          entity_type,
          account_id,
          ANY_PENDING,
          customerProfiles,
          updated_date,
          phone_number,
          addresses,
          phones,
          endUsers,
          supportingDocuments,
          // also add sinks, subscriptions, other entities here
          ...filteredPayload
        } = payload

        const addressObj = Object.values(addresses || {})?.[0]
        const endUsersByType = Object.values(endUsers || {}).reduce(
          (acc, el) => {
            acc[el?.type] = el
            return acc
          },
          {},
        )
        const {
          customer_profile_business_information,
          authorized_representative_1,
          us_a2p_messaging_profile_information,
        } = endUsersByType

        const iso_country = payload?.iso_country || addressObj?.isoCountry
        const business_type =
          payload?.business_type ||
          customer_profile_business_information?.attributes?.business_type
        const company_type =
          payload?.company_type ||
          us_a2p_messaging_profile_information?.attributes?.company_type
        const business_name =
          payload?.business_name ||
          customer_profile_business_information?.attributes?.business_name
        const website_url =
          payload?.website_url ||
          customer_profile_business_information?.attributes?.website_url
        const business_industry =
          payload?.business_industry ||
          customer_profile_business_information?.attributes?.business_industry
        const street = payload?.street || addressObj?.street
        const street_secondary =
          payload?.street_secondary || addressObj?.streetSecondary
        const city = payload?.city || addressObj?.city
        const postal_code = payload?.postal_code || addressObj?.postalCode
        const first_name =
          payload?.first_name ||
          authorized_representative_1?.attributes?.first_name
        const last_name =
          payload?.last_name ||
          authorized_representative_1?.attributes?.last_name
        const job_position =
          payload?.job_position ||
          authorized_representative_1?.attributes?.job_position
        const business_title =
          payload?.business_title ||
          authorized_representative_1?.attributes?.business_title
        const email =
          payload?.email || authorized_representative_1?.attributes?.email
        const business_registration_number =
          payload?.business_registration_number ||
          customer_profile_business_information?.attributes
            ?.business_registration_number
        const phoneWithoutCountryCode =
          phone_number?.slice?.(2) ||
          authorized_representative_1?.attributes?.phone_number?.slice?.(2)

        return {
          phone_number: phoneWithoutCountryCode,
          ...filteredPayload,
          iso_country,
          business_type,
          company_type,
          business_name,
          website_url,
          business_industry,
          street,
          street_secondary,
          city,
          postal_code,
          first_name,
          last_name,
          job_position,
          business_title,
          email,
          business_registration_number,
        }
      }
      return state
    case UPDATE_FORM_FIELD:
      return {
        ...state,
        [action.payload.fieldName]: action.payload.value,
      }
    default:
      return state
  }
}

// Combine reducers
export default combineReducers({
  entities: entityReducer,
  formFields: formFieldReducer,
})
